import { A, useNavigate, useSearchParams } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import type {
  CatalogDraftDocument,
  PlaygroundCatalogMetric,
} from "contracts/api/playground/draft";
import { createSignal, For, Show } from "solid-js";
import { createStore, unwrap } from "solid-js/store";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { diffDocument, toEditable } from "./draftDocument";
import { MetricDraftCard } from "./MetricDraftCard";
import { draftEditorKey, invalidateDrafts } from "./playgroundQueries";

export function DraftEditor(props: { draftId?: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Return to the bench that opened the editor (RunParamsBar passes ?from=…);
  // fall back to the index when opened directly.
  const returnTo = () => {
    const from = searchParams.from;
    return typeof from === "string" ? from : "/playground";
  };
  const source = useQuery(() => ({
    queryKey: draftEditorKey(props.draftId),
    queryFn: async () => {
      const catalog = await api.playground.catalog();
      const draft = props.draftId
        ? await api.playground.getDraft(props.draftId)
        : null;
      return { catalog, draft };
    },
    staleTime: Number.POSITIVE_INFINITY,
  }));

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href={returnTo()} class="landing-brand">
          ← Back
        </A>
        <span class="landing-tag">Scoring guide editor</span>
      </div>
      <div class="landing-main">
        <Show
          when={source.data}
          fallback={<div class="loading">Loading catalog…</div>}
        >
          {(data) => (
            <EditorBody
              canonical={data().catalog.metrics}
              initialLabel={data().draft?.label ?? ""}
              initialDocument={data().draft?.document ?? { metrics: {} }}
              draftId={props.draftId}
              onSaved={() => {
                invalidateDrafts();
                navigate(returnTo());
              }}
            />
          )}
        </Show>
      </div>
    </main>
  );
}

function EditorBody(props: {
  canonical: PlaygroundCatalogMetric[];
  initialLabel: string;
  initialDocument: CatalogDraftDocument;
  draftId?: string;
  onSaved: () => void;
}) {
  const [label, setLabel] = createSignal(props.initialLabel);
  const [metrics, setMetrics] = createStore(
    toEditable(props.canonical, props.initialDocument),
  );
  const action = createAsyncAction((cause) =>
    cause instanceof Error ? cause.message : String(cause),
  );

  const changedKeys = () =>
    Object.keys(diffDocument(props.canonical, unwrap(metrics)).metrics);

  const save = () => {
    if (!label().trim() || action.busy()) return;
    void action.run(async () => {
      const document = diffDocument(props.canonical, unwrap(metrics));
      const input = { label: label().trim(), document };
      if (props.draftId) {
        await api.playground.updateDraft(props.draftId, input);
      } else {
        await api.playground.createDraft(input);
      }
      props.onSaved();
    });
  };

  return (
    <>
      <div class="landing-head-row">
        <div>
          <h1 class="landing-h1">
            {props.draftId ? "Edit scoring guide" : "New scoring guide"}
          </h1>
          <p class="landing-sub pg-lede">
            A scoring guide changes how a run is scored without touching the
            canonical metrics. This one changes {changedKeys().length} metric
            {changedKeys().length === 1 ? "" : "s"}
            {changedKeys().length > 0 ? `: ${changedKeys().join(", ")}` : ""}.
            Editing a guide already used by a run saves a new version.
          </p>
          <Show when={action.error()}>
            <p class="badge-sev sev-critical">{action.error()}</p>
          </Show>
        </div>
        <div class="landing-head-row">
          <input
            class="editor-input"
            placeholder="Guide name"
            value={label()}
            onInput={(event) => setLabel(event.currentTarget.value)}
          />
          <button
            class="btn btn-primary"
            type="button"
            disabled={action.busy() || !label().trim()}
            onClick={save}
          >
            Save guide
          </button>
        </div>
      </div>
      <For each={metrics}>
        {(metric, metricIndex) => (
          <MetricDraftCard
            metric={metric}
            index={metricIndex}
            setMetrics={setMetrics}
          />
        )}
      </For>
    </>
  );
}
